/**
 * GameState 分片注册表（架构重构 P3）。
 * 各领域自持状态 Schema 并在此注册，runtime 不再静态聚合领域 Schema。
 *
 * 用法（领域侧 <domain>GameStateSlice.ts）：
 * 1. `registerGameStateSlice(key, schema, createDefault?)` 注册分片与新档默认值；
 * 2. `declare module` 增补 GameStateSlices 接口，为 GameState 提供类型字段；
 * 3. 注册文件由 app/gameStateSlices.ts 清单统一挂载（测试经 vitest setupFiles 挂载），
 *    必须在首次读档/建档前完成。
 */

import type { ZodTypeAny } from 'zod';

// ============================================
// 类型聚合接口（各领域通过 declare module 增补）
// ============================================

/**
 * GameState 的领域分片字段集合。
 * runtime 侧保持为空接口；字段由各领域的分片注册文件增补。
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface GameStateSlices {}

// ============================================
// 注册表状态
// ============================================

type SliceDefaultFactory = () => unknown;

const sliceSchemas = new Map<string, ZodTypeAny>();
const sliceDefaults = new Map<string, SliceDefaultFactory>();
let revision = 0;

// ============================================
// 公开方法
// ============================================

/**
 * 注册一个状态分片。重复注册同名 key 时忽略（幂等，兼容测试环境多路径挂载）。
 * @param key GameState 顶层字段名
 * @param schema 分片 Zod Schema（顶层通常应为 optional，兼容残缺存档）
 * @param createDefault 新档默认值工厂；不提供则新档中该分片缺省
 */
export function registerGameStateSlice(key: string, schema: ZodTypeAny, createDefault?: SliceDefaultFactory): void {
  if (sliceSchemas.has(key)) return;
  sliceSchemas.set(key, schema);
  if (createDefault) sliceDefaults.set(key, createDefault);
  revision += 1;
}

/**
 * 当前已注册分片的 shape 快照（供 Schema 组装）。
 */
export function getGameStateSliceShape(): Record<string, ZodTypeAny> {
  return Object.fromEntries(sliceSchemas);
}

/**
 * 收集各分片的新档默认值。
 */
export function collectGameStateSliceDefaults(): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  for (const [key, factory] of sliceDefaults) {
    defaults[key] = factory();
  }
  return defaults;
}

/**
 * 注册版本号：分片集合变化时递增，供 Schema 缓存失效判断。
 */
export function getGameStateSliceRevision(): number {
  return revision;
}
